<script setup>
/**
 * BcsWizardSteps - A reusable component for displaying wizard-style progress steps
 * 
 * This component displays a series of connected steps with different states:
 * - empty: Steps that have not been reached yet
 * - active: The currently active step
 * - completed: Steps that have been completed
 * - partial: Steps that have been partially completed
 * 
 * The component matches the BCS design system with purple theme for active and completed steps.
 * 
 * @example
 * <BcsWizardSteps 
 *   :steps="[
 *     { title: 'Step 1', status: 'completed' },
 *     { title: 'Step 2', status: 'active' },
 *     { title: 'Step 3', status: 'empty' }
 *   ]"
 *   :activeStep="1"
 *   @step-clicked="handleStepClick"
 * />
 * 
 * @prop {Array} steps - An array of step objects with { title, status } properties
 *    - title: String - The display name for the step
 *    - status: String - Optional status ('empty', 'active', 'completed', 'partial')
 *              If not provided, status will be determined based on activeStep prop
 * @prop {Number} activeStep - The index of the currently active step (0-based)
 * 
 * @emits {step-clicked} - Emitted when a step is clicked, with the step index as parameter
 */
import { ref, onMounted, watch, nextTick, defineExpose } from 'vue'
import checkIcon from '@/assets/images/checkIcon.svg'

const props = defineProps({
  // Array of step objects that define the wizard
  steps: {
    type: Array,
    required: true,
    // Each step should have: { title: String, status: 'empty' | 'active' | 'completed' | 'partial' }
  },
  // Current active step index (0-based)
  activeStep: {
    type: Number,
    default: 0
  },
  wrapLabels: {
    type: Boolean,
    default: false
  },
  // When false, the step label shows only step.title without the auto-prepended "N. " prefix.
  showNumber: {
    type: Boolean,
    default: true
  },
  // Horizontal alignment of the step label text.
  labelAlign: {
    type: String,
    default: 'center',
    validator: (value) => ['left', 'center', 'right'].includes(value)
  },
  // When false, the colored progress line between steps is hidden.
  showProgressLine: {
    type: Boolean,
    default: true
  },
  // When true, steps become interactive (pointer + keyboard) and emit 'step-clicked'.
  // Kept opt-in so existing consumers stay non-interactive and unaffected.
  clickableSteps: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['step-clicked'])

// Only interactive steps emit and expose button semantics; guards keyboard handlers too.
const activateStep = (index) => {
  if (!props.clickableSteps) return
  emit('step-clicked', index)
}

// Refs for tracking circle positions
const wrapperRef = ref(null);
const circleRefs = ref([]);

// Compute status classes for each step
const getStepClasses = (index) => {
  // Get the step status or determine it based on active step
  const step = props.steps[index]
  const status = step.status || (
    index === props.activeStep ? 'active' : 
    index < props.activeStep ? 'completed' : 
    'empty'
  )

  // Return the appropriate classes
  return {
    'active': status === 'active',
    'completed': status === 'completed',
    'empty': status === 'empty',
    'partial': status === 'partial'
  }
}

// Store for actual positions of steps
const stepPositions = ref([]);
const progressBarWidth = ref('0%');
const barsPosition = ref({ left: '0%', width: '100%' });

// Set up the ref for a circle
const setCircleRef = (el, index) => {
  if (!circleRefs.value[index]) {
    circleRefs.value[index] = { element: null };
  }
  circleRefs.value[index].element = el;
};

// Calculate positions based on actual DOM measurements
const calculatePositions = () => {
  // Wait until the DOM is updated
  nextTick(() => {
    if (!wrapperRef.value) return;
    
    // Initialize the circles array if needed
    if (circleRefs.value.length === 0) {
      circleRefs.value = Array(props.steps.length).fill().map(() => ({ element: null }));
    }
    
    const wrapperRect = wrapperRef.value.getBoundingClientRect();
    const wrapperWidth = wrapperRect.width;
    
    // Get all circle elements that have been successfully referenced
    const circleElements = circleRefs.value
      .filter(ref => ref && ref.element)
      .map(ref => ref.element);
    
    // If we don't have any circles, we can't calculate positions
    if (circleElements.length === 0) {
      return;
    }
    
    // Calculate positions of all circles
    const positions = circleElements.map(element => {
      const rect = element.getBoundingClientRect();
      // Calculate center position relative to the wrapper
      const center = rect.left + rect.width / 2 - wrapperRect.left;
      return (center / wrapperWidth) * 100;
    });
    
    stepPositions.value = positions;
    
    // Calculate the bars position (from first to last step)
    if (positions.length > 1) {
      const firstPos = positions[0];
      const lastPos = positions[positions.length - 1];
      
      barsPosition.value = {
        left: `${firstPos}%`,
        width: `${lastPos - firstPos}%`
      };
      
      // Calculate progress bar width based on active step
      if (props.activeStep === 0) {
        progressBarWidth.value = '0%';
      } else if (props.activeStep >= props.steps.length) {
        progressBarWidth.value = '100%';
      } else {
        // Find the position of the active step
        const activePos = positions[Math.min(props.activeStep, positions.length - 1)];
        const progressWidth = activePos - firstPos;
        progressBarWidth.value = `${progressWidth}%`;
      }
    }
  });
};

// Function to force recalculation with multiple attempts
const recalculatePositions = () => {
  setTimeout(() => {
    calculatePositions();
  }, 300);
};

// Add resize event listener
const addResizeListener = () => {
  window.addEventListener('resize', calculatePositions);
};

// Remove resize event listener
const removeResizeListener = () => {
  window.removeEventListener('resize', calculatePositions);
};

// Watch for changes that would affect the layout
onMounted(() => {
  // Initialize the circles array
  circleRefs.value = Array(props.steps.length).fill().map(() => ({ element: null }));
  addResizeListener();
  calculatePositions();
});

// Watch for changes in active step
watch(() => props.activeStep, () => {
  calculatePositions();
});

// Watch for changes in steps array
watch(() => props.steps.length, () => {
  // Reinitialize the circles array if the number of steps changes
  circleRefs.value = Array(props.steps.length).fill().map(() => ({ element: null }));
  calculatePositions();
});

// Expose methods to parent components
defineExpose({
  recalculatePositions,
  addResizeListener,
  removeResizeListener
});
</script>

<template>
  <div class="wizard-steps-wrapper" ref="wrapperRef">
    <!-- Gray progress bar background -->
    <div class="progress-bar-bg" :style="barsPosition"></div>
    
    <!-- Active progress bar (colored line) -->
    <div
      v-if="showProgressLine"
      class="progress-bar-active"
      :style="{
        ...barsPosition,
        width: progressBarWidth
      }"></div>
    
    <!-- Steps container -->
    <div class="wizard-container d-flex align-items-center justify-content-between">
      <template v-for="(step, index) in steps" :key="index">
        <div
          class="wizard-step-container"
          :class="{ 'wizard-step-container--clickable': clickableSteps }"
          :role="clickableSteps ? 'button' : null"
          :tabindex="clickableSteps ? 0 : null"
          :aria-label="clickableSteps ? step.title : null"
          @click="activateStep(index)"
          @keydown.enter.prevent="activateStep(index)"
          @keydown.space.prevent="activateStep(index)">
          <div class="wizard-step" :class="[getStepClasses(index), { 'wrap-label': wrapLabels }]">
            <div class="circle mx-auto" :ref="el => setCircleRef(el, index)">
              <img :src="checkIcon" alt="Check">
            </div>
            <p
              class="step-text mt-2.5 mb-0"
              :title="step.title"
              :style="{ textAlign: labelAlign }">
              {{ showNumber ? ((index + 1) + '. ' + step.title) : step.title }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.wizard-steps-wrapper {
  position: relative;
  width: 100%;
}

.progress-bar-bg, .progress-bar-active {
  position: absolute;
  height: 2px;
  z-index: 0;
  top: 16px;
}

.progress-bar-bg {
  background-color: var(--bs-gray-400); /* Gray color for inactive state */
}

.progress-bar-active {
  background-color: var(--bs-primary); /* Purple color for active state */
  transition: width 0.3s ease;
}

/* Position the circles over the lines */
.wizard-container {
  position: relative;
  width: 100%;
  z-index: 1;
}

.wizard-step-container {
  position: relative;
  z-index: 1;
}

/* Interactive steps (opt-in via clickableSteps) get pointer affordance + focus ring. */
.wizard-step-container--clickable {
  cursor: pointer;
}

.wizard-step-container--clickable:focus-visible {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Ensure the background of circles covers the line */
.circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding-top: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  transition: all 0.3s ease;
  border: 3px solid;
  font-size: 16px;
  background-color: white; /* Ensure the circle covers the line */
}

.wizard-step {
  display: flex;
  flex-direction: column;
  text-align: center;
}

.step-text {
  color: var(--Eggplant---Dark, #2E0C59);
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
}

.wizard-step.wrap-label .step-text {
  max-width: none;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

/* Step status styling */
.wizard-step.empty .circle {
  background-color: #ffffff;
  border-color: var(--bs-gray-300);
  color: #6C757D;
  img {
    visibility: hidden;
  }
}

.wizard-step.empty .step-text {
  color: #6C757D;
}

.wizard-step.active .circle {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
  box-shadow:
    inset 0 0 0 8px #ffffff,
    inset 0 0 0 80px var(--bs-primary),
    0 0 0 4px #7b61ff78;
  border: 2px solid var(--bs-primary) !important;
  img {
    visibility: hidden;
  }
}

.wizard-step.active .step-text {
  color: var(--bs-primary);
  font-weight: bold;
}

.wizard-step.completed .circle {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
}

.wizard-step.completed .circle img {
  visibility: visible;
}

.wizard-step.completed .step-text {
  color: var(--bs-primary);
  font-weight: bold;
}

.wizard-step.partial .circle {
  background-color: #ffffff;
  border-color: var(--bs-primary);
  color: var(--bs-primary);
  img {
    visibility: hidden;
  }
}

.wizard-step.partial .step-text {
  color: var(--bs-primary);
  font-weight: bold;
}

@media (max-width: 768px) {
  .step-text {
    font-size: 0.7rem;
    max-width: 70px;
  }
  
  .circle {
    width: 25px;
    height: 25px;
  }
}
</style>
